/**
 * O documento em HTML — e o HTML de volta ao documento.
 *
 * Esta é a ponte entre o nosso modelo (seções com blocos) e o **editor de texto**
 * (TinyMCE), que trabalha em HTML. A ponte precisa ser **fiel nos dois sentidos**:
 * o que está guardado sai igual para o editor, e o que o editor devolve vira o mesmo
 * documento — sem perder bloco, sem perder id de seção (as figuras apontam para a
 * seção por id) e sem deixar entrar formatação que o papel não sabe imprimir.
 *
 * Três decisões que estão no código:
 *
 * 1. **o HTML é nosso, não da web.** O gerador escreve e lê exatamente as tags que
 *    ele mesmo produz (título, parágrafo, citação, lista, tabela, `strong`/`em`).
 *    O que vier de fora (colagem, um editor antigo) tem as tags jogadas fora e o
 *    **texto mantido** — perder formatação é aceitável, perder texto não;
 * 2. **o título da seção é o `h1`/`h2`/`h3` do editor**, e o nível do título é o
 *    nível da seção. O número (`2.1`) não entra no HTML: quem numera é o motor, a
 *    partir da ordem — e um número digitado à mão viraria número errado no papel;
 * 3. **os ids são preservados por posição e por título** (`reaproveitarIds`): mexer
 *    no texto não pode reescrever o id de todas as seções, senão a figura que
 *    apontava para a seção 3 passa a apontar para lugar nenhum.
 */
import { blocosDaSecao } from './blocos.js';
import { tabelaRetangular } from './blocos.js';
import { tituloSemNumero } from './abnt.js';
import { ehTagDeTitulo, nivelDaTag, tagDoNivel } from './tipos.js';
/* ------------------------------------------------------------------ */
/* Escrita: documento → HTML                                           */
/* ------------------------------------------------------------------ */
/** As marcas do texto (`**negrito**`, `*itálico*`) em HTML, com o texto escapado. */
function marcasEmHtml(texto) {
    const escapado = texto
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    return escapado
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*\n]+)\*/g, '<em>$1</em>');
}
/**
 * O atributo do recuo, quando o bloco tem algum.
 *
 * O recuo viaja como **atributo** (`data-recuo`), e não como `style`: o editor
 * trabalha com HTML, e um `margin-left` solto seria jogado fora na leitura (o
 * gerador só entende as tags e os atributos que ele mesmo escreve). O atributo é
 * um número de degraus — quem transforma degrau em milímetro é o motor
 * (`recuoDeBlocoMm`), na hora de desenhar.
 */
function atributoDoRecuo(bloco) {
    const degrau = Math.round(Number(bloco.recuo ?? 0));
    return degrau > 0 ? ` data-recuo="${degrau}"` : '';
}
/** O recuo lido de um nó do HTML (ausente ou zero = o bloco na margem). */
function recuoDoNo(no) {
    const bruto = Number.parseInt(no.atributos['data-recuo'] ?? '', 10);
    return Number.isFinite(bruto) && bruto > 0 ? bruto : undefined;
}
/**
 * Um bloco do documento em HTML.
 *
 * `numeroDaNota` chega de fora porque o número **não mora no bloco**: quem manda é a posição
 * da nota no documento (mover uma nota renumera as outras), e quem conta é quem percorre as
 * seções.
 */
export function blocoEmHtml(bloco, numeroDaNota) {
    if (bloco.tipo === 'IMAGEM') {
        const url = (bloco.url ?? '').trim();
        /* Imagem sem arquivo não vira HTML: legenda sozinha não é figura. */
        if (!url)
            return '';
        const legenda = bloco.legenda?.trim() ? ` data-legenda="${escaparAtributo(bloco.legenda.trim())}"` : '';
        const fonte = bloco.fonte?.trim() ? ` data-fonte="${escaparAtributo(bloco.fonte.trim())}"` : '';
        const largura = bloco.larguraMm ? ` data-largura="${Math.round(bloco.larguraMm)}"` : '';
        /*
         * A legenda e a fonte viajam como **atributos** (como na tabela): no editor a pessoa
         * mexe na figura (envia o arquivo, arrasta), e a legenda e a fonte se escrevem no painel
         * — sem os atributos, elas se perderiam a cada edição.
         */
        return `<figure${legenda}${fonte}${largura}><img src="${escaparAtributo(url)}" alt="${escaparAtributo(bloco.legenda?.trim() ?? '')}"></figure>`;
    }
    if (bloco.tipo === 'NOTA') {
        /*
         * No texto a nota é só o **número** em sobrescrito. O texto da nota viaja no atributo:
         * é assim que ele sobrevive à ida e volta pelo editor, onde ele não se escreve (quem o
         * escreve é o painel das notas).
         */
        const numero = numeroDaNota ?? '';
        const texto = bloco.texto?.trim() ? ` data-nota-texto="${escaparAtributo(bloco.texto.trim())}"` : '';
        return `<sup data-nota="${numero}"${texto}>${numero}</sup>`;
    }
    if (bloco.tipo === 'LISTA') {
        const itens = (bloco.itens ?? []).map((item) => `  <li>${marcasEmHtml(item)}</li>`);
        if (itens.length === 0)
            return '';
        const tag = bloco.lista === 'NUMERADA' ? 'ol' : 'ul';
        return `<${tag}${atributoDoRecuo(bloco)}>\n${itens.join('\n')}\n</${tag}>`;
    }
    if (bloco.tipo === 'TABELA') {
        const linhas = tabelaRetangular(bloco.linhas ?? []);
        if (linhas.length === 0)
            return '';
        const [cabecalho, ...corpo] = linhas;
        const celulas = (linha, tag) => `    <tr>${linha.map((celula) => `<${tag}>${marcasEmHtml(celula)}</${tag}>`).join('')}</tr>`;
        /*
         * A legenda e a fonte viajam como **atributos** da tabela: no editor a pessoa
         * edita a grade (é o que ele faz bem), e a legenda e a fonte se escrevem no
         * painel do documento. Sem os atributos, elas se perderiam a cada edição.
         */
        const legenda = bloco.legenda?.trim() ? ` data-legenda="${escaparAtributo(bloco.legenda.trim())}"` : '';
        const fonte = bloco.fonte?.trim() ? ` data-fonte="${escaparAtributo(bloco.fonte.trim())}"` : '';
        return `<table${legenda}${fonte}>\n  <thead>\n${celulas(cabecalho ?? [], 'th')}\n  </thead>\n  <tbody>\n${corpo
            .map((linha) => celulas(linha, 'td'))
            .join('\n')}\n  </tbody>\n</table>`;
    }
    const texto = bloco.texto ?? '';
    if (bloco.tipo === 'CITACAO') {
        return texto.trim() ? `<blockquote${atributoDoRecuo(bloco)}>${marcasEmHtml(texto)}</blockquote>` : '';
    }
    /*
     * O recuo do parágrafo vem junto: sem ele, o que a pessoa recuou na tela sairia
     * colado na margem na volta do editor.
     */
    return `<p${atributoDoRecuo(bloco)}>${marcasEmHtml(texto)}</p>`;
}
/** O texto de um atributo HTML, escapado. */
function escaparAtributo(valor) {
    return valor.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
/**
 * O corpo do documento (as seções do texto) em HTML, para o editor.
 *
 * Só as **seções** entram: resumo, abstract, referências, figuras e folha continuam
 * nos painéis, que é onde eles são campos e não texto corrido.
 */
export function documentoParaHtml(documento) {
    const partes = [];
    /* A numeração das notas é do documento inteiro, na ordem em que elas aparecem. */
    let notas = 0;
    for (const secao of documento.secoes) {
        const tag = tagDoNivel(secao.nivel || 1);
        partes.push(`<${tag}>${marcasEmHtml(secao.titulo.trim())}</${tag}>`);
        for (const bloco of blocosDaSecao(secao)) {
            const numeroDaNota = bloco.tipo === 'NOTA' ? (notas += 1) : undefined;
            const html = blocoEmHtml(bloco, numeroDaNota);
            if (html)
                partes.push(html);
        }
    }
    return partes.join('\n');
}
/** Tags que não têm fechamento (ou que fecham sozinhas entre nós). */
const SEM_FECHAMENTO = new Set(['br', 'img', 'hr', 'input', 'meta', 'link']);
/**
 * Lê o HTML em uma árvore de nós.
 *
 * Um analisador pequeno e **estrito** de propósito: ele entende as tags que o
 * gerador escreve e as que o editor produz a partir delas — não é um analisador de
 * HTML da web, e não precisa ser. O que não é reconhecido vira **texto** (o conteúdo
 * nunca se perde; a tag, sim).
 */
export function lerHtml(html) {
    const raiz = { tag: '#raiz', atributos: {}, filhos: [] };
    const pilha = [raiz];
    const partes = html.split(/(<[^>]*>)/g);
    for (const parte of partes) {
        if (!parte)
            continue;
        const atual = pilha[pilha.length - 1];
        if (!parte.startsWith('<')) {
            atual.filhos.push(desescapar(parte));
            continue;
        }
        if (parte.startsWith('<!--'))
            continue;
        if (parte.startsWith('<!'))
            continue;
        const fechando = /^<\//.test(parte);
        const achado = parte.match(/^<\/?\s*([a-zA-Z0-9]+)((?:\s+[^>]*?)?)\/?>$/);
        if (!achado) {
            /* Não é uma tag que a gente entenda: entra como texto, para não sumir. */
            atual.filhos.push(desescapar(parte));
            continue;
        }
        const tag = achado[1].toLowerCase();
        if (fechando) {
            /* Fecha até achar esta tag (o HTML do editor é bem formado; isto é a rede). */
            const indice = pilha.map((no) => no.tag).lastIndexOf(tag);
            if (indice > 0)
                pilha.length = indice;
            continue;
        }
        const no = { tag, atributos: atributosDe(achado[2] ?? ''), filhos: [] };
        atual.filhos.push(no);
        if (!SEM_FECHAMENTO.has(tag) && !parte.endsWith('/>'))
            pilha.push(no);
    }
    return raiz.filhos;
}
/** Os atributos que interessam, de dentro da tag. */
function atributosDe(trecho) {
    const atributos = {};
    const achados = trecho.matchAll(/([a-zA-Z-]+)\s*=\s*"([^"]*)"/g);
    for (const achado of achados)
        atributos[achado[1].toLowerCase()] = desescapar(achado[2] ?? '');
    return atributos;
}
/**
 * As entidades nomeadas que aparecem num texto de trabalho acadêmico.
 *
 * **Por que isto existe:** o editor de texto (e o Word, e o HTML de fora) escreve o
 * acento como entidade — "á" chega como `&aacute;`. Sem esta tabela o papel imprimia
 * **`par&aacute;grafo`**, com o código no lugar da letra: o gerador guardava a
 * entidade como se fosse texto, e nenhum desenho tinha como adivinhar o acento.
 *
 * A lista é a do Latin-1 (as letras acentuadas do português e das outras línguas
 * europeias) mais a pontuação que um editor costuma escrever. Entidade desconhecida
 * **fica como está** — melhor um código à vista do que sumir texto.
 */
const ENTIDADES = {
    /* o básico */
    nbsp: ' ',
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    /* pontuação e símbolos do Latin-1 */
    iexcl: '¡', cent: '¢', pound: '£', curren: '¤', yen: '¥', brvbar: '¦', sect: '§', uml: '¨',
    copy: '©', ordf: 'ª', laquo: '«', not: '¬', shy: '', reg: '®', macr: '¯', deg: '°',
    plusmn: '±', sup2: '²', sup3: '³', acute: '´', micro: 'µ', para: '¶', middot: '·',
    cedil: '¸', sup1: '¹', ordm: 'º', raquo: '»', frac14: '¼', frac12: '½', frac34: '¾',
    iquest: '¿', times: '×', divide: '÷',
    /* as letras acentuadas — maiúsculas */
    Agrave: 'À', Aacute: 'Á', Acirc: 'Â', Atilde: 'Ã', Auml: 'Ä', Aring: 'Å', AElig: 'Æ', Ccedil: 'Ç',
    Egrave: 'È', Eacute: 'É', Ecirc: 'Ê', Euml: 'Ë', Igrave: 'Ì', Iacute: 'Í', Icirc: 'Î', Iuml: 'Ï',
    ETH: 'Ð', Ntilde: 'Ñ', Ograve: 'Ò', Oacute: 'Ó', Ocirc: 'Ô', Otilde: 'Õ', Ouml: 'Ö', Oslash: 'Ø',
    Ugrave: 'Ù', Uacute: 'Ú', Ucirc: 'Û', Uuml: 'Ü', Yacute: 'Ý', THORN: 'Þ',
    /* as letras acentuadas — minúsculas */
    szlig: 'ß', agrave: 'à', aacute: 'á', acirc: 'â', atilde: 'ã', auml: 'ä', aring: 'å', aelig: 'æ',
    ccedil: 'ç', egrave: 'è', eacute: 'é', ecirc: 'ê', euml: 'ë', igrave: 'ì', iacute: 'í',
    icirc: 'î', iuml: 'ï', eth: 'ð', ntilde: 'ñ', ograve: 'ò', oacute: 'ó', ocirc: 'ô',
    otilde: 'õ', ouml: 'ö', oslash: 'ø', ugrave: 'ù', uacute: 'ú', ucirc: 'û', uuml: 'ü',
    yacute: 'ý', thorn: 'þ', yuml: 'ÿ',
    /* o resto da Europa, que aparece em nome de autor */
    OElig: 'Œ', oelig: 'œ', Scaron: 'Š', scaron: 'š', Yuml: 'Ÿ', fnof: 'ƒ',
    /* pontuação de texto corrido */
    ndash: '–', mdash: '—', lsquo: '‘', rsquo: '’', ldquo: '“', rdquo: '”', sbquo: '‚',
    bdquo: '„', dagger: '†', Dagger: '‡', bull: '•', hellip: '…', permil: '‰',
    lsaquo: '‹', rsaquo: '›', euro: '€', trade: '™', prime: '′', Prime: '″',
    ensp: ' ', emsp: ' ', thinsp: ' ', zwnj: '', zwj: '', lrm: '', rlm: '',
};
/** Um código numérico vira a letra que ele representa (código inválido, nada). */
function deCodigo(codigo) {
    return Number.isFinite(codigo) && codigo > 0 && codigo <= 0x10ffff ? String.fromCodePoint(codigo) : '';
}
/**
 * As entidades do HTML de volta para as letras.
 *
 * `&amp;` fica por último de propósito: quem escreveu `&amp;aacute;` quer ver
 * `&aacute;` no texto, e não `á` — decodificar antes disso inventaria um acento que
 * ninguém escreveu.
 */
function desescapar(texto) {
    return texto
        .replace(/&#(\d+);/g, (achado, codigo) => deCodigo(Number(codigo)) || achado)
        .replace(/&#[xX]([0-9a-fA-F]+);/g, (achado, codigo) => deCodigo(parseInt(codigo, 16)) || achado)
        .replace(/&([a-zA-Z][a-zA-Z0-9]*);/g, (achado, nome) => nome === 'amp' ? achado : (ENTIDADES[nome] ?? achado))
        .replace(/&amp;/g, '&');
}
/**
 * O texto de um nó, em Markdown: negrito e itálico voltam como `**` e `*`.
 *
 * A quebra de linha (`br`) vira **espaço**: no nosso modelo o parágrafo é um bloco
 * de texto corrido, e um `br` sobrevivendo aqui viraria uma linha quebrada que o
 * papel imprime como espaço — o defeito silencioso que já custou caro neste projeto.
 */
function textoDoNo(no, marcas) {
    if (typeof no === 'string') {
        const marcasAbertas = marcas.negrito && marcas.italico ? '***' : marcas.negrito ? '**' : marcas.italico ? '*' : '';
        return marcasAbertas ? `${marcasAbertas}${no}${marcasAbertas}` : no;
    }
    if (no.tag === 'br')
        return ' ';
    if (no.tag === 'strong' || no.tag === 'b') {
        return no.filhos.map((filho) => textoDoNo(filho, { ...marcas, negrito: true })).join('');
    }
    if (no.tag === 'em' || no.tag === 'i') {
        return no.filhos.map((filho) => textoDoNo(filho, { ...marcas, italico: true })).join('');
    }
    /* Tag desconhecida: o texto dela entra, a tag não. */
    return no.filhos.map((filho) => textoDoNo(filho, marcas)).join('');
}
/** O texto de um nó, aparado (é o que se grava num bloco). */
function textoLimpo(no) {
    return textoDoNo(no, { negrito: false, italico: false }).replace(/\s+/g, ' ').trim();
}
/** Os blocos de dentro de um elemento de bloco (um `td` de tabela, por exemplo). */
/**
 * A imagem que está **dentro** deste nó (ou nele mesmo, quando ele é a figura).
 *
 * São três os formatos que chegam do editor: o `<figure>` que o gerador escreve, o `<img>`
 * solto e o `<img>` dentro de um parágrafo — o último é o caso comum, porque quem insere a
 * figura é o editor de texto, que a põe na linha do cursor.
 */
/**
 * O endereço da imagem como o gerador guarda: a partir da **raiz** (`/uploads/...`).
 *
 * O editor de texto às vezes devolve o caminho **relativo à página** (`../uploads/...`),
 * e endereço que sobe diretório é recusado pela varredura de segurança da API — a figura
 * derrubava a gravação do documento inteiro. Aqui o endereço volta para a raiz, e o que já
 * estiver gravado assim também se conserta ao salvar.
 */
function enderecoDaImagem(bruto) {
    const limpo = bruto.trim();
    if (!limpo)
        return '';
    if (/^[a-z]+:\/\//i.test(limpo))
        return limpo;
    const semSubidas = limpo.replace(/^(\.\.?\/)+/, '');
    return `/${semSubidas}`.replace(/^\/{2,}/, '/');
}
function imagemDoNo(no) {
    /*
     * A procura é **rasa** de propósito: a figura é o nó (`figure`/`img`) ou uma imagem que é
     * filha direta dele (o editor põe a figura na linha do cursor, e a linha é um `p`).
     *
     * Uma busca funda aqui era um defeito: a imagem que estava dentro de um `blockquote` fazia
     * o leitor tratar a **citação** como parágrafo e perder o que havia dentro dela — a nota,
     * inclusive. Quem varre o interior dos blocos de texto é `marcadoresDoNo`.
     */
    const filhoImagem = (atual) => {
        if (atual.tag === 'img')
            return atual;
        /* Na figura o `img` está **dentro** dela; no parágrafo, ele é filho direto. */
        const dentro = atual.filhos.find((filho) => typeof filho !== 'string' && (filho.tag === 'img' || (atual.tag === 'figure' && filho.tag === 'img')));
        return dentro ?? null;
    };
    const img = filhoImagem(no);
    if (!img)
        return null;
    /* Os atributos da figura: do `figure` quando ele existe, e do próprio `img` quando não. */
    const dono = no.tag === 'figure'
        ? no
        : (no.filhos.find((f) => typeof f !== 'string' && f.tag === 'figure') ?? img);
    const legenda = (dono.atributos['data-legenda'] ?? '').trim();
    const fonte = (dono.atributos['data-fonte'] ?? '').trim();
    const largura = Number.parseInt(dono.atributos['data-largura'] ?? '', 10);
    return {
        id: '',
        tipo: 'IMAGEM',
        url: enderecoDaImagem(img.atributos.src ?? ''),
        ...(legenda ? { legenda } : {}),
        ...(fonte ? { fonte } : {}),
        ...(Number.isFinite(largura) && largura > 0 ? { larguraMm: largura } : {}),
    };
}
/**
 * As **notas** que estão dentro de um nó cujo texto virou um bloco.
 *
 * O caso comum: `<p>Palavra<sup data-nota="1">1</sup></p>`. O parágrafo vira um bloco de
 * texto — e o texto é lido **sem** as marcas, então a nota que estava no meio dele se
 * perderia na primeira gravação, em silêncio. Aqui ela é recolhida e vira um bloco próprio,
 * logo depois do texto a que pertence (é onde o papel a desenha, como o número da nota).
 */
function marcadoresDoNo(no) {
    const marcadores = [];
    const percorrer = (atual) => {
        if (atual.tag === 'sup' && atual.atributos['data-nota'] !== undefined) {
            marcadores.push({ id: '', tipo: 'NOTA', texto: (atual.atributos['data-nota-texto'] ?? '').trim() });
            return;
        }
        /* A figura que está dentro do bloco (o editor a põe na linha do cursor). */
        const imagem = imagemDoNo(atual);
        if (imagem) {
            marcadores.push(imagem);
            return;
        }
        atual.filhos.forEach((filho) => {
            if (typeof filho !== 'string')
                percorrer(filho);
        });
    };
    no.filhos.forEach((filho) => {
        if (typeof filho !== 'string')
            percorrer(filho);
    });
    return marcadores;
}
function blocosDeUmaTabela(no) {
    const linhas = [];
    const percorrer = (atual) => {
        if (atual.tag === 'tr') {
            const celulas = atual.filhos
                .filter((filho) => typeof filho !== 'string' && (filho.tag === 'td' || filho.tag === 'th'))
                .map((celula) => textoLimpo(celula));
            if (celulas.length > 0)
                linhas.push(celulas);
            return;
        }
        atual.filhos.forEach((filho) => {
            if (typeof filho !== 'string')
                percorrer(filho);
        });
    };
    percorrer(no);
    if (linhas.length === 0)
        return null;
    return {
        id: '',
        tipo: 'TABELA',
        legenda: no.atributos['data-legenda'] ?? '',
        fonte: no.atributos['data-fonte'] ?? '',
        linhas: tabelaRetangular(linhas),
    };
}
/** Os blocos de texto dentro de um nó (o corpo de uma seção). */
function blocosDoNo(filhos) {
    const blocos = [];
    const textoSolto = [];
    const fecharTextoSolto = () => {
        const texto = textoSolto.join(' ').replace(/\s+/g, ' ').trim();
        textoSolto.length = 0;
        if (texto)
            blocos.push({ id: '', tipo: 'PARAGRAFO', texto });
    };
    for (const filho of filhos) {
        if (typeof filho === 'string') {
            textoSolto.push(filho);
            continue;
        }
        const tag = filho.tag;
        if (ehTagDeTitulo(tag))
            continue; // tratado por quem chama
        /*
         * A nota: o número em sobrescrito, com o texto dela no atributo. Ela vem antes de tudo
         * porque dentro de um parágrafo (`<p>texto<sup>1</sup></p>`) ela seria comida junto com
         * o texto.
         */
        if (tag === 'sup' && filho.atributos['data-nota'] !== undefined) {
            /* O texto solto **antes** da nota fecha em parágrafo: "palavra[1]" não é a mesma linha. */
            fecharTextoSolto();
            const texto = (filho.atributos['data-nota-texto'] ?? '').trim();
            blocos.push({ id: '', tipo: 'NOTA', texto });
            continue;
        }
        /*
         * A figura: o `figure`, o `img` solto ou o `img` dentro de um parágrafo. Quando o nó tem
         * texto **e** imagem (o editor põe a figura na linha do cursor, que pode ter texto), o
         * texto sobra como parágrafo — nada se perde, e a figura sai logo depois dele.
         */
        const imagem = imagemDoNo(filho);
        if (imagem) {
            const textoDoNo = textoLimpo(filho);
            fecharTextoSolto();
            if (textoDoNo)
                blocos.push({ id: '', tipo: 'PARAGRAFO', texto: textoDoNo, recuo: recuoDoNo(filho) });
            blocos.push(imagem);
            continue;
        }
        if (tag === 'ul' || tag === 'ol') {
            fecharTextoSolto();
            const itens = filho.filhos
                .filter((item) => typeof item !== 'string' && item.tag === 'li')
                .map((item) => textoLimpo(item))
                .filter(Boolean);
            if (itens.length > 0) {
                blocos.push({
                    id: '',
                    tipo: 'LISTA',
                    lista: tag === 'ol' ? 'NUMERADA' : 'MARCADA',
                    itens,
                    recuo: recuoDoNo(filho),
                });
                blocos.push(...marcadoresDoNo(filho));
            }
            continue;
        }
        if (tag === 'table') {
            fecharTextoSolto();
            const tabela = blocosDeUmaTabela(filho);
            if (tabela) {
                blocos.push(tabela);
                blocos.push(...marcadoresDoNo(filho));
            }
            continue;
        }
        if (tag === 'blockquote') {
            fecharTextoSolto();
            const texto = textoLimpo(filho);
            if (texto)
                blocos.push({ id: '', tipo: 'CITACAO', texto, recuo: recuoDoNo(filho) });
            blocos.push(...marcadoresDoNo(filho));
            continue;
        }
        if (tag === 'p' || tag === 'div' || tag === 'section' || tag === 'article') {
            fecharTextoSolto();
            const texto = textoLimpo(filho);
            if (texto)
                blocos.push({ id: '', tipo: 'PARAGRAFO', texto, recuo: recuoDoNo(filho) });
            /* A nota que estava no meio do parágrafo vem logo depois dele. */
            blocos.push(...marcadoresDoNo(filho));
            continue;
        }
        /* Qualquer outra tag: o texto dela entra no parágrafo em construção. */
        textoSolto.push(textoLimpo(filho));
    }
    fecharTextoSolto();
    /* O id de cada bloco nasce depois, do id da seção (é o que o painel usa). */
    return blocos;
}
/**
 * As seções do HTML lido, com os títulos e os níveis.
 *
 * Conteúdo **antes** do primeiro título entra na primeira seção existente (quem
 * apaga todos os títulos não pode perder o texto).
 */
function secoesDoHtml(html) {
    const raiz = lerHtml(html);
    const secoes = [];
    let atuais = [];
    let cabecalho = null;
    const fechar = () => {
        if (!cabecalho && atuais.length === 0)
            return;
        /*
         * Conteúdo **sem título** é o que a pessoa fez ao apagar a linha do título: no
         * editor, aquele texto passou a ficar embaixo do título anterior. Então ele entra
         * na seção anterior, em vez de virar uma seção sem nome no papel — e, se for o
         * caso do texto começar sem título nenhum, ele fica com o título da primeira
         * seção do documento (ver `aplicarHtmlAoDocumento`).
         */
        if (!cabecalho) {
            const blocos = blocosDoNo(atuais);
            const anterior = secoes[secoes.length - 1];
            if (anterior)
                anterior.blocos.push(...blocos);
            else
                secoes.push({ titulo: '', nivel: 1, blocos });
            atuais = [];
            return;
        }
        const nivel = nivelDaTag(cabecalho.tag);
        secoes.push({
            titulo: textoLimpo(cabecalho),
            nivel,
            blocos: blocosDoNo(atuais),
        });
        atuais = [];
        cabecalho = null;
    };
    for (const filho of raiz) {
        if (typeof filho !== 'string' && ehTagDeTitulo(filho.tag)) {
            fechar();
            cabecalho = filho;
            /*
             * Nota ou figura **dentro do título** não se perde: o título é lido como texto, e os
             * marcadores que estavam nele viram blocos da seção que ele abre. Sem isto, uma nota
             * inserida na linha do título desapareceria na primeira gravação — em silêncio, que é o
             * tipo de perda que só se descobre depois de imprimir.
             */
            const marcadores = filho.filhos.filter((interno) => typeof interno !== 'string' && (interno.tag === 'img' || interno.tag === 'sup' || interno.tag === 'figure'));
            if (marcadores.length > 0)
                atuais = marcadores;
            continue;
        }
        atuais.push(filho);
    }
    fechar();
    return secoes;
}
/**
 * O HTML do editor de volta ao documento.
 *
 * O que o editor devolve vira **seções com blocos**, e os ids das seções que
 * continuam existindo são preservados (`reaproveitarIds`) — é o que mantém as
 * figuras ligadas à seção certa depois de qualquer edição.
 */
export function aplicarHtmlAoDocumento(documento, html) {
    const lidas = secoesDoHtml(html);
    if (lidas.length === 0)
        return documento;
    /*
     * Texto que veio **sem título nenhum** (a pessoa apagou o primeiro): ele fica com o
     * título da primeira seção que já existia, em vez de virar uma seção sem nome.
     */
    if (!lidas[0].titulo)
        lidas[0].titulo = documento.secoes[0]?.titulo ?? '';
    const ids = reaproveitarIds(documento.secoes, lidas.map((secao) => secao.titulo));
    const secoes = lidas.map((lida, indice) => {
        const id = ids[indice];
        const blocos = lida.blocos.map((bloco, ordem) => ({ ...bloco, id: `${id}-b${ordem}` }));
        /*
         * O espelho (`paragrafos`, `citacaoLonga`) é reescrito aqui, como em qualquer
         * outra edição: quem só conhece os campos antigos continua lendo o texto certo.
         */
        const citacao = blocos
            .filter((bloco) => bloco.tipo === 'CITACAO')
            .map((bloco) => bloco.texto ?? '')
            .filter((texto) => texto.trim())
            .join('\n\n');
        return {
            ...documento.secoes[indice],
            id,
            /*
             * O número digitado no título **sai** aqui também: é o momento em que o texto do
             * editor vira documento guardado, e o título que sobe limpo é o que a pessoa vê
             * ao reabrir (e o que o painel de blocos mostra). O desenho ainda tira o número
             * (`numerarSecoes`), porque documento salvo por fora deste caminho — a API, uma
             * versão anterior — também precisa sair certo.
             */
            titulo: tituloSemNumero(lida.titulo),
            nivel: lida.nivel,
            blocos,
            paragrafos: blocos.filter((bloco) => bloco.tipo === 'PARAGRAFO').map((bloco) => bloco.texto ?? ''),
            ...(citacao ? { citacaoLonga: citacao } : { citacaoLonga: undefined }),
        };
    });
    return { ...documento, secoes };
}
/**
 * Os ids das seções lidas, aproveitando os que já existiam.
 *
 * Primeiro por **posição com o mesmo título** (o caso comum: a pessoa escreveu,
 * ninguém renomeou nada), depois por **título** entre as seções que sobraram, e só
 * então um id novo. Sem isto, qualquer edição reescreveria os ids todos, e as
 * figuras — que apontam para a seção por id — perderiam o lugar.
 */
function reaproveitarIds(existentes, titulos) {
    const usados = new Set();
    const ids = titulos.map(() => null);
    /*
     * A comparação é feita **sem o número digitado**: o título chega do editor com o
     * número (a pessoa acabou de digitá-lo) e o que está guardado já vem limpo da última
     * gravação. Sem isto, salvar trocaria o id da seção e a figura que aponta para ela
     * perderia o lugar.
     */
    const guardado = existentes.map((secao) => normalizar(tituloSemNumero(secao.titulo)));
    const lido = titulos.map((titulo) => normalizar(tituloSemNumero(titulo)));
    lido.forEach((titulo, indice) => {
        const naMesmaPosicao = existentes[indice];
        if (naMesmaPosicao && guardado[indice] === titulo && !usados.has(naMesmaPosicao.id)) {
            ids[indice] = naMesmaPosicao.id;
            usados.add(naMesmaPosicao.id);
        }
    });
    lido.forEach((titulo, indice) => {
        if (ids[indice])
            return;
        const posicao = guardado.findIndex((guardado, outra) => !usados.has(existentes[outra].id) && guardado === titulo);
        if (posicao >= 0) {
            const igual = existentes[posicao];
            ids[indice] = igual.id;
            usados.add(igual.id);
        }
    });
    return ids.map((id, indice) => id ?? `secao-${indice + 1}-${Date.now().toString(36)}`);
}
/** Compara títulos sem depender de espaço, caixa e acento. */
function normalizar(texto) {
    return texto
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
}
//# sourceMappingURL=html.js.map